import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { toInt } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { useDistributorScope } from '../hooks/useDistributorScope';
import { unwrapLookupItems, digitsOnly, isExactDigits } from '../utils/format';
import { withSeparatorDelete } from '../utils/phone';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldTextarea,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';
import { useFullRecordRefill } from './detail/useFullRecordRefill';

// Distribütör (Distributor) tam-sayfa detay. CRUD ApiResponse envelope uçları:
//  GET  /api/distributors/GetById?Id={guid}
//  POST /api/distributors/Add  · PUT /api/distributors/Update · PUT /api/distributors/Delete
const API_BASE = '/api/distributors';

const STATUS_META = {
  1: { label: 'Aktif', tone: 'ok' },
  0: { label: 'Pasif', tone: 'neutral' },
};
const STATUS_OPTIONS = [
  { value: '', label: 'Seçiniz' },
  ...Object.entries(STATUS_META).map(([value, meta]) => ({ value: Number(value), label: meta.label })),
];

// #822 vendorId (Distribütör) — zorunlu değil; boş = tüm distribütörler için geçerli genel tanım.
// #771.3 identityNumber (TC Kimlik No) + salesRepresentativeId (Satış Temsilcisi) — ikisi de opsiyonel.
const EMPTY_FORM = { textId: '', name: '', phone: '', address: '', status: 1, vendorId: '', identityNumber: '', salesRepresentativeId: '' };

// #771.1 Telefon: formatTrPhone (utils/phone.js) her zaman başa '0' zorluyordu → kullanıcı alan
// kodunu/0'ı silemiyordu. Bu ekran için zorla-0 YAPMAYAN hafif yerel biçimlendirme: rakamları
// (xxxx) xxx xx xx düzeninde grupla ama eksik/silinmiş haneye 0 ekleme (serbestçe düzenlenebilir).
//
// #771.1 (2. tur) Bu tek başına YETMEDİ: değer "(0532)" iken Backspace metnin son karakterini,
// yani AYIRAÇ ")" işaretini siliyor; rakamlar değişmediği için biçimlendirici onu anında geri
// yazıyordu → tuş hiç çalışmıyor gibi görünüyor, alan kodu silinemiyordu. `withSeparatorDelete`
// sarmalayıcısı ham metin kısaldığı hâlde rakamlar aynıysa son rakamı düşürerek bunu çözer.
function formatPhoneLooseBase(v) {
  let d = String(v ?? '').replace(/\D/g, '');
  // Yapıştırılan +90 / 90 ülke kodunu at (10'dan uzun ve 90 ile başlıyorsa).
  if (d.startsWith('90') && d.length > 10) d = d.slice(2);
  d = d.slice(0, 11);
  if (!d) return '';
  const a = d.slice(0, 4), b = d.slice(4, 7), c = d.slice(7, 9), e = d.slice(9, 11);
  let out = '(' + a;
  if (d.length >= 4) out += ')';
  if (b) out += ' ' + b;
  if (c) out += ' ' + c;
  if (e) out += ' ' + e;
  return out;
}
const formatPhoneLoose = withSeparatorDelete(formatPhoneLooseBase);
// #771.1 Telefon geçerlilik: opsiyonel ama GİRİLDİYSE anlamlı rakam sayısı 10 (5xx…) veya 11 (0…) olmalı.
const phoneDigitCount = (v) => String(v ?? '').replace(/\D/g, '').length;

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

const rowToForm = (row) => ({
  textId: pick(row, 'textId', 'TextId') ?? '',
  name: pick(row, 'name', 'Name') ?? '',
  phone: pick(row, 'phone', 'Phone') ?? '',
  address: pick(row, 'address', 'Address') ?? '',
  status: pick(row, 'status', 'Status') ?? 1,
  vendorId: pick(row, 'vendorId', 'VendorId') ?? '',
  // #771.3
  identityNumber: pick(row, 'identityNumber', 'IdentityNumber') ?? '',
  salesRepresentativeId: pick(row, 'salesRepresentativeId', 'SalesRepresentativeId') ?? '',
});

export function DistributorDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Distribütör' : 'Distribütör Detayı', 'Distribütör tanımı.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);
  // #771.3 Satış Temsilcisi seçici için lookup — vendorId'nin FieldSelect deseniyle aynı.
  const [repOptions, setRepOptions] = useState([{ value: '', label: 'Seçiniz' }]);

  // #822 Distribütör kapsamı — seçenekler kullanıcının yetkili olduğu distribütörlerdir
  // (/api/vendors/Select zaten UserDataScope ile daraltılmış gelir).
  const { vendorOptions, lockedOptions, isDistributorUser, lockedVendorId, scopeLoaded } = useDistributorScope();

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  // #771.2 Kullanıcı forma dokunduysa arka plandaki tam-kayıt tazelemesi onu ezmesin.
  const { markDirty, refillIfClean } = useFullRecordRefill();
  const set = (k, v) => { markDirty(); setForm((c) => ({ ...c, [k]: v })); };

  // Tek kayıt yükleme: önce location.state.row (listeden geldiyse); yoksa GetById ile çek.
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    if (location.state?.row) {
      const sr = location.state.row;
      setForm(rowToForm(sr));
      // #771.2 Liste satırı (c360 $select) YALNIZ görünür kolonları taşır → Adres ve
      // TC Kimlik No formda boş görünüyordu (kayıt DB'de dolu olduğu hâlde). Tam kaydı
      // fire-and-forget çekip formu ve audit'i ondan tazele; anlık çizimi bloklamaz.
      fetch(`${API_BASE}/GetById?Id=${encodeURIComponent(id)}`, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => {
          const r = unwrap(body);
          if (!r) return;
          refillIfClean(() => setForm(rowToForm(r)));
          setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        })
        .catch(() => {});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/GetById?Id=${encodeURIComponent(id)}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Distribütör alınamadı (${res.status})`);
      const r = unwrap(await res.json());
      if (!r) throw new Error('Distribütör bulunamadı');
      setForm(rowToForm(r));
      setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Distribütör alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, location.state, refillIfClean]);

  useEffect(() => { load(); }, [load]);

  // #771.3 Satış Temsilcisi lookup'ını çek (WaybillDetailPage ile aynı uç: /api/representatives/Select).
  useEffect(() => {
    fetch('/api/representatives/Select', { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        const items = unwrapLookupItems(body);
        const opts = items.map((it) => {
          const value = pick(it, 'id', 'Id');
          const code = pick(it, 'textId', 'TextId') ?? '';
          const name = pick(it, 'name', 'Name') ?? '';
          const label = [code, name].filter(Boolean).join(' - ') || String(value);
          return { value, label };
        });
        setRepOptions([{ value: '', label: 'Seçiniz' }, ...opts]);
      })
      .catch(() => {});
  }, []);

  // #822 Distribütör tipi kullanıcı: yetkili distribütörü otomatik seçili + değiştirilemez.
  // load() bittikten sonra çalışır — aksi halde kaydın kendi vendorId'si oto-seçimi ezerdi.
  useEffect(() => {
    if (!scopeLoaded || loading || !lockedVendorId) return;
    setForm((c) => (String(c.vendorId ?? '') === lockedVendorId ? c : { ...c, vendorId: lockedVendorId }));
  }, [scopeLoaded, loading, lockedVendorId]);

  // Klonla: /distributors/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — Kaydet'e kadar).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildPayload = () => ({
    textId: form.textId.trim(),
    name: form.name.trim(),
    phone: form.phone || null,
    address: form.address || null,
    status: toInt(form.status) ?? 1,
    // #822 Boş = tüm distribütörler için geçerli genel tanım → backend'e null gitmeli ('' değil).
    // Update attach-all olduğu için bu alan HER kaydetmede gönderilmeli; gönderilmezse null'lanır.
    vendorId: form.vendorId || null,
    // #771.3 TC Kimlik No + Satış Temsilcisi — opsiyonel; boşsa null (attach-all → her kaydetmede gönder).
    identityNumber: (form.identityNumber || '').trim() || null,
    salesRepresentativeId: form.salesRepresentativeId || null,
  });

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp yeni boş kayda hazırlan.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.textId.trim() || !form.name.trim()) {
      showToast('error', 'Kod ve Ad zorunludur.'); return;
    }
    // #771.1 Telefon opsiyonel; ama girildiyse geçerli olmalı (10 veya 11 anlamlı rakam).
    const pd = phoneDigitCount(form.phone);
    if (pd > 0 && pd < 10) {
      showToast('error', 'Telefon numarası eksik/geçersiz. Alan kodu dahil en az 10 hane girin veya alanı boş bırakın.'); return;
    }
    // #771.3 TC Kimlik No opsiyonel; girildiyse tam 11 hane olmalı.
    if (!isExactDigits(form.identityNumber, 11)) {
      showToast('error', 'TC Kimlik No 11 haneli olmalıdır.'); return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(isNew ? payload : { id, ...payload }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || (isNew ? 'Kayıt eklenemedi' : 'Kayıt güncellenemedi'));
      if (isNew) {
        if (andNew) {
          showToast('success', 'Distribütör eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Distribütör başarıyla eklendi.');
        setTimeout(() => navigate('/distributors'), 400);
        return;
      }
      showToast('success', 'Distribütör başarıyla güncellendi.');
      setTimeout(() => navigate('/distributors'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut form bilgilerini taşıyarak yeni-kayıt ekranını aç. Kullanıcı Kaydet diyene kadar hiçbir şey kaydedilmez.
  const handleClone = () => {
    navigate('/distributors/new', { state: { clone: { ...form } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || 'Kayıt silinemedi');
      // #771.4 Backend hareket varsa silmez, pasife alır → Data.passivated = true döner.
      const data = pick(body, 'Data', 'data');
      if (data && (data.passivated || data.Passivated)) {
        setDeleteConfirm(false);
        setForm((c) => ({ ...c, status: 0 }));
        showToast('error', pick(body, 'Message', 'message') || 'Distribütör kullanımda olduğu için silinemedi; pasife alındı.');
        setSaving(false);
        return;
      }
      showToast('success', 'Distribütör başarıyla silindi.');
      setTimeout(() => navigate('/distributors'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme işlemi sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const statusMeta = STATUS_META[form.status];
  const statusBadge = !isNew && statusMeta
    ? <StatusBadge tone={statusMeta.tone}>{statusMeta.label}</StatusBadge>
    : null;

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/distributors')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni Distribütör aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Distribütörler', to: '/distributors' }, { label: isNew ? 'Yeni Distribütör' : 'Distribütör Detayı', current: true }]}
      status={statusBadge}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'Distributor', entityId: id }}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="box" title="Distribütör Bilgileri" subtitle="Kod, ad, iletişim ve durum bilgileri">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="textId" label="Kod" req mono />
            <FieldText form={form} set={set} tried={tried} k="name" label="Ad" req />
            {/* #771.1 zorla-0 YOK: alan kodu/0 serbestçe silinebilir; girildiyse Kaydet'te uzunluk denetlenir. */}
            <FieldText form={form} set={set} k="phone" label="Telefon" placeholder="(0532) 222 22 22" format={formatPhoneLoose} hint="Opsiyonel — girilirse alan kodu dahil en az 10 hane." />
            <FieldSelect form={form} set={set} k="status" label="Durum" options={STATUS_OPTIONS} />
            {/* #771.3 */}
            {/* #771.3 TC Kimlik No: yalnız rakam, en fazla 11 hane (harf girişi engellenir). */}
            <FieldText form={form} set={set} k="identityNumber" label="TC Kimlik No" mono
                       placeholder="Opsiyonel" format={digitsOnly(11)} hint="11 haneli, yalnız rakam" />
            <FieldSelect form={form} set={set} k="salesRepresentativeId" label="Satış Temsilcisi" options={repOptions} hint="Opsiyonel — bağlı satış temsilcisi." />
            {/* #822 Distribütör — zorunlu değil. Boş bırakılırsa Distribütör tüm distribütörler için
                geçerlidir; seçilirse yalnız o distribütörün kullanıcılarına listelenir. Distribütör
                tipi kullanıcıda yetkili distribütör otomatik seçili ve değiştirilemez. */}
            <FieldSelect
              form={form} set={set} k="vendorId" label="Distribütör"
              options={isDistributorUser && lockedVendorId ? lockedOptions : vendorOptions}
              disabled={Boolean(isDistributorUser && lockedVendorId)}
              hint={isDistributorUser && lockedVendorId
                ? 'Distribütör tipi kullanıcı — yetkili distribütörünüz otomatik seçildi.'
                : 'Boş bırakılırsa tüm distribütörler için geçerli olur.'}
            />
            <FieldTextarea form={form} set={set} k="address" label="Adres" rows={2} span />
          </Grid>
        </Section>
      </div>

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.name}”</strong> Distribütörünü silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default DistributorDetailPage;
